import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { CateringOffer } from './data/smaken';
export interface RequestLine extends CateringOffer {
  /** Number of people (or units, for 'per styck' / 'per bricka' items). */
  quantity: number;
}
interface CartContextType {
  lines: RequestLine[];
  add: (offer: CateringOffer, quantity?: number) => void;
  setQuantity: (id: string, quantity: number) => void;
  remove: (id: string) => void;
  clear: () => void;
  count: number;
  total: number;
}
const STORAGE_KEY = 'smaken-request-v1';
const CartContext = createContext<CartContextType | undefined>(undefined);
export function CartProvider({
  children
}: {
  children: ReactNode;
}) {
  const [lines, setLines] = useState<RequestLine[]>([]);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) setLines(JSON.parse(raw));
    } catch {
      /* ignore unreadable storage */
    }
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch {
      /* ignore write failures */
    }
  }, [lines]);
  const add = (offer: CateringOffer, quantity?: number) => {
    const initial = quantity ?? Math.max(offer.minGuests, 1);
    setLines(prev => {
      const existing = prev.find(l => l.id === offer.id);
      if (existing) {
        return prev.map(l => l.id === offer.id ? {
          ...l,
          quantity: l.quantity + initial
        } : l);
      }
      return [...prev, {
        ...offer,
        quantity: initial
      }];
    });
  };
  const setQuantity = (id: string, quantity: number) => {
    setLines(prev => quantity < 1 ? prev.filter(l => l.id !== id) : prev.map(l => l.id === id ? {
      ...l,
      quantity
    } : l));
  };
  const remove = (id: string) => setLines(prev => prev.filter(l => l.id !== id));
  const clear = () => setLines([]);
  const count = lines.length;
  const total = lines.reduce((sum, l) => sum + l.price * l.quantity, 0);
  return <CartContext.Provider value={{
    lines,
    add,
    setQuantity,
    remove,
    clear,
    count,
    total
  }} data-id="element-35" data-mp="6rtbkven9oo4">
      {children}
    </CartContext.Provider>;
}
export function useRequest() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useRequest must be used within a CartProvider');
  }
  return context;
}